import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, ChevronRight, Loader2, RefreshCw, Sparkles } from 'lucide-react';
import toast from '../../../../../components/Toast';
import {
  directorApi,
  DirectorApiError,
  type TrinityCardItem,
  type TrinityCardsGenerateInput,
  type TrinityCardSlot,
  type TrinityReferenceAssetResolution,
  type TrinityCardsTask,
} from '../../../services/directorApi';
import { ShotTrinityCardsDialog } from '../ShotTrinityCardsDialog';

interface Props {
  shotId: string;
  shotPrompt: string;
  injectedCharacterIds: string[];
  injectedPropIds: string[];
  injectedSceneId: string | null;
  injectedCharacterNames: string[];
  injectedPropNames: string[];
  injectedSceneName: string | null;
  currentCredits: number;
  videoActive: boolean;
  initialCards: TrinityCardItem[];
  onShotUpdated: () => void;
}

const IN_PROGRESS_STATUSES = new Set<TrinityCardItem['status']>([
  'waiting_dependency',
  'pending',
  'submitting',
  'processing',
]);

function sameCards(a: TrinityCardItem[], b: TrinityCardItem[]) {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function ShotTrinityCardsTab({
  shotId,
  shotPrompt,
  injectedCharacterIds,
  injectedPropIds,
  injectedSceneId,
  injectedCharacterNames,
  injectedPropNames,
  injectedSceneName,
  currentCredits,
  videoActive,
  initialCards,
  onShotUpdated,
}: Props) {
  const [cards, setCards] = useState<TrinityCardItem[]>(initialCards);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [aiCost, setAiCost] = useState<number | null>(null);
  const [aiEnabled, setAiEnabled] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [task, setTask] = useState<TrinityCardsTask | null>(null);
  const [referenceAssets, setReferenceAssets] = useState<TrinityReferenceAssetResolution | null>(null);
  const [referenceAssetsLoading, setReferenceAssetsLoading] = useState(false);
  const lastTaskStateRef = useRef<string | null>(null);

  // 仅在弹窗关闭时同步 initialCards（摘要 API 不含候选图，弹窗开启期间不能覆盖完整数据）
  useEffect(() => {
    if (!dialogOpen) {
      setCards(initialCards);
    }
  }, [initialCards, dialogOpen]);

  const refreshTask = useCallback(async () => {
    const next = await directorApi.getCurrentTrinityCardsTask(shotId);
    setTask(next);
    return next;
  }, [shotId]);

  useEffect(() => {
    refreshTask().catch(() => undefined);
  }, [refreshTask]);

  useEffect(() => {
    directorApi
      .getPublicSettings()
      .then((res) => {
        setAiCost(res.trinityCardsCreditsPerCall);
        setAiEnabled(res.trinityCardsLlmEnabled);
      })
      .catch(() => {
        // 静默失败，不阻塞基础使用
      });
  }, []);

  const completedCount = useMemo(
    () => cards.filter((card) => card.status === 'completed').length,
    [cards]
  );
  const readyCount = completedCount;
  const failedCount = useMemo(
    () => cards.filter((card) => card.status === 'failed').length,
    [cards]
  );
  const inProgressCount = useMemo(
    () => cards.filter((card) => IN_PROGRESS_STATUSES.has(card.status)).length,
    [cards]
  );
  const taskRunning = task?.status === 'pending' || task?.status === 'running';
  const cardsInProgress = useMemo(
    () => cards.some((card) => IN_PROGRESS_STATUSES.has(card.status)),
    [cards]
  );
  const actionLocked = !!busyKey || taskRunning || cardsInProgress || videoActive;

  const refreshCards = useCallback(
    async (notifyProject = false) => {
      const next = await directorApi.listTrinityCards(shotId);
      let changed = false;
      setCards((prev) => {
        changed = !sameCards(prev, next);
        return changed ? next : prev;
      });
      if (changed && notifyProject) {
        onShotUpdated();
      }
      return next;
    },
    [onShotUpdated, shotId]
  );

  // 弹窗打开时拉取完整卡片数据（含候选图）
  useEffect(() => {
    if (!dialogOpen) return;
    refreshCards().catch(() => undefined);
  }, [dialogOpen, refreshCards]);

  useEffect(() => {
    if (!task) {
      lastTaskStateRef.current = null;
      return;
    }
    const key = `${task.taskId}:${task.status}`;
    if (lastTaskStateRef.current === key) return;

    const isFirstUpdate = lastTaskStateRef.current === null;
    lastTaskStateRef.current = key;

    if (task.status === 'completed') {
      if (!isFirstUpdate) {
        const creditsUsed = task.result?.creditsUsed;
        toast.success(
          typeof creditsUsed === 'number'
            ? `任务已完成（消耗 ${creditsUsed} 积分）`
            : '任务已完成'
        );
        refreshCards(true).catch(() => undefined);
      }
      return;
    }
    if (task.status === 'failed' && task.error?.message) {
      if (!isFirstUpdate) {
        toast.error(task.error.message);
      }
    }
  }, [task, refreshCards]);

  const isPolling = taskRunning || cards.some((card) => IN_PROGRESS_STATUSES.has(card.status));

  // SSE 订阅：任务流式文案实时推送（替代 1s 轮询任务状态）
  useEffect(() => {
    if (!taskRunning) return;
    const ctrl = directorApi.subscribeTrinityTaskStream(shotId, (event) => {
      if (event.type === 'update' && event.task) {
        setTask(event.task);
      } else if (event.type === 'done') {
        // SSE 服务端已关闭，拉取最新任务状态 + 刷新卡片
        refreshTask().catch(() => undefined);
        refreshCards(true).catch(() => undefined);
      } else if (event.type === 'error') {
        // SSE 断开或异常，回退到轮询
        refreshTask().catch(() => undefined);
      }
    });
    return () => ctrl.abort();
  }, [taskRunning, shotId, refreshTask, refreshCards]);

  // 卡片状态轮询：仅刷新卡片（图片生成阶段需要），不再轮询任务
  useEffect(() => {
    if (!isPolling) return;
    const timer = window.setInterval(() => {
      refreshCards(true).catch(() => undefined);
    }, 3000);
    return () => window.clearInterval(timer);
  }, [isPolling, refreshCards]);

  const ensurePromptReady = () => {
    if (!shotPrompt.trim()) {
      toast.error('请先填写分镜提示词，再生成三合一卡');
      return false;
    }
    return true;
  };

  const currentInput = useMemo(
    () => ({
      prompt: shotPrompt,
      injectedCharacterIds,
      injectedPropIds,
      injectedSceneId,
    }),
    [shotPrompt, injectedCharacterIds, injectedPropIds, injectedSceneId]
  );

  useEffect(() => {
    if (!dialogOpen) return;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      setReferenceAssetsLoading(true);
      directorApi
        .resolveTrinityReferenceAssets(shotId, currentInput)
        .then((result) => {
          if (!cancelled) {
            setReferenceAssets(result);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setReferenceAssets(null);
          }
        })
        .finally(() => {
          if (!cancelled) {
            setReferenceAssetsLoading(false);
          }
        });
    }, 200);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [dialogOpen, shotId, currentInput]);

  /** 单卡文案生成 */
  const handleGeneratePrompt = async (
    slot: TrinityCardSlot,
    config?: Pick<TrinityCardsGenerateInput, 'modelId' | 'quality'>
  ) => {
    if (actionLocked) return;
    if (!aiEnabled) {
      toast.error('三合一卡 AI 融合暂未开启，请联系管理员');
      return;
    }
    if (!ensurePromptReady()) return;
    setBusyKey(`prompt:${slot}`);
    try {
      const result = await directorApi.generateSingleTrinityCardPrompt(shotId, slot, {
        ...currentInput,
        ...config,
        forceOverwriteDraft: true,
      });
      setTask(result);
      toast.success(
        result.alreadyRunning
          ? '已有任务在进行中，正在接续查看进度'
          : 'AI 文案生成已启动'
      );
      await refreshCards(false);
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '文案生成失败');
    } finally {
      setBusyKey(null);
    }
  };

  /** 单卡图片生成 */
  const handleGenerateImage = async (
    slot: TrinityCardSlot,
    config?: Pick<TrinityCardsGenerateInput, 'modelId' | 'quality'>
  ) => {
    if (actionLocked) return;
    setBusyKey(`image:${slot}`);
    try {
      const result = await directorApi.generateSingleTrinityCardImage(shotId, slot, config);
      const slotLabel = slot === 'role' ? '角色卡' : slot === 'story' ? '故事卡' : '视频卡';
      toast.success(`已开始生成${slotLabel}图（消耗 ${result.imageCreditsUsed} 积分）`);
      await refreshCards(true);
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '图片生成失败');
    } finally {
      setBusyKey(null);
    }
  };

  /** 锁定候选图为当前图 */
  const handleLockCandidate = async (slot: TrinityCardSlot, imageJobId: string) => {
    if (videoActive) return;
    setBusyKey(`lock:${slot}`);
    try {
      await directorApi.lockTrinityCardCandidate(shotId, slot, imageJobId);
      toast.success('已锁定为当前图');
      await refreshCards(true);
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '锁定失败');
    } finally {
      setBusyKey(null);
    }
  };

  /** 删除单个候选图 */
  const handleDeleteCandidate = async (slot: TrinityCardSlot, imageJobId: string) => {
    if (videoActive) return;
    setBusyKey(`del-candidate:${slot}`);
    try {
      await directorApi.deleteTrinityCardCandidate(shotId, slot, imageJobId);
      toast.success('候选图已删除');
      await refreshCards(true);
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '删除失败');
    } finally {
      setBusyKey(null);
    }
  };

  const handleRefresh = () => {
    Promise.allSettled([refreshCards(true), refreshTask()]).catch((err) => {
      toast.error(err instanceof DirectorApiError ? err.message : '刷新失败');
    });
  };

  return (
    <>
      <div className="space-y-4">
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-[0.16em] text-violet-600">
                <Sparkles className="h-3.5 w-3.5" />
                三合一参考
              </div>
              <p className="mt-1 text-sm text-slate-600">
                生成与管理已移至独立弹窗，右侧编辑区更简洁。
              </p>
              <p className="mt-1 text-xs text-slate-500">
                链路固定为 角色卡 -&gt; 故事卡 -&gt; 视频卡，视频卡同时继承角色身份。
              </p>
            </div>
            <div className="shrink-0 rounded-2xl bg-slate-50 px-3 py-2 text-right ring-1 ring-slate-200">
              <div className="text-lg font-semibold leading-none text-slate-900">{completedCount}/3</div>
              <div className="mt-1 text-[11px] text-slate-500">已完成</div>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
            <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-slate-700">
              就绪 {readyCount}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-amber-700">
              生成中 {inProgressCount}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2.5 py-1 text-rose-700">
              失败 {failedCount}
            </span>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-slate-600">
            {typeof aiCost === 'number' && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-700">
                AI 融合 {aiCost} 积分/次
              </span>
            )}
            {!aiEnabled && (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-amber-700">
                <AlertTriangle className="h-3 w-3" />
                当前未开放 AI 融合
              </span>
            )}
            {videoActive && (
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">
                视频已激活，修改卡片前请先取消或重激活
              </span>
            )}
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setDialogOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-xl bg-violet-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-violet-500"
            >
              管理三合一参考图
              <ChevronRight className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={handleRefresh}
              disabled={!!busyKey}
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-violet-200 hover:text-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busyKey ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              刷新状态
            </button>
          </div>
        </div>
      </div>

      <ShotTrinityCardsDialog
        open={dialogOpen}
        shotId={shotId}
        cards={cards}
        busyKey={busyKey}
        task={task}
        aiCost={aiCost}
        aiEnabled={aiEnabled}
        videoActive={videoActive}
        completedCount={completedCount}
        currentCredits={currentCredits}
        injectedAssets={{
          characters: injectedCharacterNames,
          props: injectedPropNames,
          scene: injectedSceneName,
        }}
        referenceAssets={referenceAssets}
        referenceAssetsLoading={referenceAssetsLoading}
        onClose={() => setDialogOpen(false)}
        onRefresh={handleRefresh}
        onGeneratePrompt={handleGeneratePrompt}
        onGenerateImage={handleGenerateImage}
        onSavePrompt={async (slot, prompt) => {
          try {
            await directorApi.saveTrinityCardPrompt(shotId, slot, prompt);
            toast.success('提示词已保存');
            await refreshCards(true);
          } catch (err) {
            toast.error(err instanceof DirectorApiError ? err.message : '保存失败');
          }
        }}
        onLockCandidate={handleLockCandidate}
        onDeleteCandidate={handleDeleteCandidate}
      />
    </>
  );
}
